import React from "react";
import { CountryCode } from "./region";

// Small flag pictures for the four firm countries. Emoji flags (🇨🇦) don't show
// on Windows (it prints "CA" or nothing), so the app draws its own (Roadmap
// 1883 follow-up, owner 2026-10-25). Simplified to read well at ~20 px.

const star = (cx: number, cy: number, r: number, points = 5): string => {
  const inner = r * (points === 7 ? 0.45 : 0.4);
  const out: string[] = [];
  for (let i = 0; i < points * 2; i += 1) {
    const radius = i % 2 === 0 ? r : inner;
    const angle = (Math.PI / points) * i - Math.PI / 2;
    out.push(
      `${(cx + radius * Math.cos(angle)).toFixed(2)},${(
        cy +
        radius * Math.sin(angle)
      ).toFixed(2)}`,
    );
  }
  return out.join(" ");
};

/** Union Jack drawn in a 60 x 30 box (used by GB and the AU canton). */
const UnionJack: React.FC = () => (
  <g>
    <rect width={60} height={30} fill={"#012169"} />
    <path d={"M0,0 L60,30 M60,0 L0,30"} stroke={"#FFFFFF"} strokeWidth={6} />
    <path d={"M0,0 L60,30 M60,0 L0,30"} stroke={"#C8102E"} strokeWidth={2} />
    <path d={"M30,0 V30 M0,15 H60"} stroke={"#FFFFFF"} strokeWidth={10} />
    <path d={"M30,0 V30 M0,15 H60"} stroke={"#C8102E"} strokeWidth={6} />
  </g>
);

const CANADA_LEAF =
  "M30,6 L31.8,9.9 L33.9,9 L33,14.1 L36.3,11.4 L36.9,13.2 L39.9,12.6 L39,15.6 L40.5,16.5 L35.4,20.7 L36,22.5 L30.9,21.9 L30.9,26 L29.1,26 L29.1,21.9 L24,22.5 L24.6,20.7 L19.5,16.5 L21,15.6 L20.1,12.6 L23.1,13.2 L23.7,11.4 L27,14.1 L26.1,9 L28.2,9.9 Z";

const FLAGS: Record<CountryCode, React.ReactNode> = {
  CA: (
    <>
      <rect width={60} height={30} fill={"#FFFFFF"} />
      <rect width={15} height={30} fill={"#D80621"} />
      <rect x={45} width={15} height={30} fill={"#D80621"} />
      <path d={CANADA_LEAF} fill={"#D80621"} />
    </>
  ),
  US: (
    <>
      <rect width={60} height={30} fill={"#FFFFFF"} />
      {Array.from({ length: 7 }, (_, i) => (
        <rect
          key={i}
          y={(i * 2 * 30) / 13}
          width={60}
          height={30 / 13}
          fill={"#B22234"}
        />
      ))}
      <rect width={24} height={(7 * 30) / 13} fill={"#3C3B6E"} />
      {Array.from({ length: 12 }, (_, i) => (
        <circle
          key={i}
          cx={3 + (i % 4) * 6}
          cy={2.6 + Math.floor(i / 4) * 5.4}
          r={1.1}
          fill={"#FFFFFF"}
        />
      ))}
    </>
  ),
  AU: (
    <>
      <rect width={60} height={30} fill={"#012169"} />
      <svg width={30} height={15} viewBox={"0 0 60 30"}>
        <UnionJack />
      </svg>
      <polygon points={star(15, 22.5, 4.2, 7)} fill={"#FFFFFF"} />
      <polygon points={star(45, 24.5, 2.2, 7)} fill={"#FFFFFF"} />
      <polygon points={star(38, 13, 2.2, 7)} fill={"#FFFFFF"} />
      <polygon points={star(45, 5.5, 2.2, 7)} fill={"#FFFFFF"} />
      <polygon points={star(51.5, 11.5, 2.2, 7)} fill={"#FFFFFF"} />
      <polygon points={star(48, 16, 1.3)} fill={"#FFFFFF"} />
    </>
  ),
  GB: <UnionJack />,
};

type Props = { code: CountryCode; className?: string };

/** A 21 x 14 flag with a thin border; decorative (the country name is next to it). */
export const FlagIcon: React.FC<Props> = ({ code, className }) => (
  <svg
    data-testid={`flag-${code}`}
    aria-hidden={true}
    width={21}
    height={14}
    viewBox={"0 0 60 30"}
    preserveAspectRatio={"xMidYMid slice"}
    className={`shrink-0 rounded-[2px] shadow-[0_0_0_1px_rgba(31,36,51,0.15)] ${
      className || ""
    }`}>
    {FLAGS[code]}
  </svg>
);
